import { clsx } from 'clsx'
import { motion } from 'framer-motion'
import { memo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { NUTRIENT_COLORS } from '@shared/config'
import { notyError } from '@shared/lib/notifications'
import { Btn } from '@shared/ui/btn'
import { NotchLoader } from '@shared/ui/loader'

import { getTarget } from '../api'

import styles from './styles.module.scss'

//
//

const MACRO_KEYS = ['proteins', 'fats', 'carbs']

const getPercent = (progress) => {
  if (!progress?.target) {
    return 0
  }

  return Math.min(100, Math.round((progress.consumed / progress.target) * 100))
}

//
//

const FCGeneralInfo = () => {
  const { t } = useTranslation()

  const [target, setTarget] = useState(null)

  // starts true - the fetch always begins on mount, avoids an error-state flash on first paint
  const [inProgress, setInProgress] = useState(true)

  useEffect(() => {
    const fn = async () => {
      const targetData = await getTarget()

      setInProgress(false)

      if (targetData?.error) {
        setTarget(false)
        notyError(t('generalInfo:error.target'))
        return
      }

      setTarget(targetData?.data)
    }

    fn()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //
  //

  const currentTarget = target?.currentTarget
  const currentProgress = target?.currentProgress

  const caloriesProgress = currentProgress?.calories

  //
  //

  const renderStatus = (progress, unit) => {
    const isExceeded = progress.status === 'over'

    return (
      <span
        className={clsx(styles.status, {
          [styles.status_exceeded]: isExceeded,
        })}
      >
        {t(`generalInfo:status.${isExceeded ? 'exceeded' : 'remaining'}`, {
          value: `${Math.abs(progress.difference ?? 0)} ${unit}`,
        })}
      </span>
    )
  }

  //
  //

  let content

  if (inProgress) {
    content = (
      <div className="d-flex justify-content-center py-4">
        <NotchLoader size={35} />
      </div>
    )
  } else if (target === false) {
    content = (
      <p className={styles.info__error}>{t('generalInfo:error.target')}</p>
    )
  } else if (!currentTarget) {
    content = <p className={styles.info__info}>{t('generalInfo:noTarget')}</p>
  } else {
    content = (
      <>
        <div className={styles.info__header}>
          <h2>{t('generalInfo:title')}</h2>

          <span className={styles.info__goal}>
            {t(`generalInfo:goals.${currentTarget.goal}`, {
              defaultValue: currentTarget.goal,
            })}
          </span>
        </div>

        {/* ************************************************************************** */}

        <div className={styles.calories}>
          <div className={styles.calories__numbers}>
            <span className={styles.calories__consumed}>
              {caloriesProgress?.consumed ?? 0}
            </span>
            <span className={styles.calories__target}>
              / {currentTarget.calories} {t('generalInfo:units.kcal')}
            </span>
          </div>

          <div className={styles.track}>
            <motion.div
              className={styles.track__fill}
              style={{ backgroundColor: NUTRIENT_COLORS.calories }}
              initial={{ width: 0 }}
              animate={{ width: `${getPercent(caloriesProgress)}%` }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            />
          </div>

          {caloriesProgress &&
            renderStatus(caloriesProgress, t('generalInfo:units.kcal'))}
        </div>

        {/* ************************************************************************** */}

        <ul className={styles.info__nutrients}>
          {MACRO_KEYS.map((key, index) => {
            const progress = currentProgress?.[key]

            if (!progress) {
              return null
            }

            return (
              <li key={key} className={styles.nutrient}>
                <div className={styles.nutrient__info}>
                  <span className={styles.nutrient__name}>
                    <i
                      className={styles.nutrient__dot}
                      style={{ backgroundColor: NUTRIENT_COLORS[key] }}
                    />
                    {t(`generalInfo:nutrients.${key}`)}
                  </span>

                  <span className={styles.nutrient__values}>
                    {progress.consumed} / {progress.target}{' '}
                    {t('generalInfo:units.g')}
                  </span>
                </div>

                <div className={styles.track}>
                  <motion.div
                    className={styles.track__fill}
                    style={{ backgroundColor: NUTRIENT_COLORS[key] }}
                    initial={{ width: 0 }}
                    animate={{ width: `${getPercent(progress)}%` }}
                    transition={{
                      duration: 0.6,
                      delay: 0.1 * (index + 1),
                      ease: 'easeOut',
                    }}
                  />
                </div>

                {renderStatus(progress, t('generalInfo:units.g'))}
              </li>
            )
          })}
        </ul>

        {/* ************************************************************************** */}
      </>
    )
  }

  //
  //

  return (
    <section className={styles.info}>
      {content}
      <Btn
        design={['primary', 'medium']}
        link="/stats"
        className={styles.info__btn}
      >
        {t('generalInfo:toStats')} <i className="fa-solid fa-chart-line ms-1" />
      </Btn>
    </section>
  )
}

export const GeneralInfo = memo(FCGeneralInfo)
